package fr.manyverse.app.presentation.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.sp
import fr.manyverse.app.AppConfig
import fr.manyverse.app.presentation.auth.rememberNativeAuthLaunchers
import fr.manyverse.app.presentation.components.onboarding.OnboardingCarousel
import fr.manyverse.app.presentation.components.onboarding.ThemeToggleButton
import fr.manyverse.app.presentation.components.onboarding.AnonymousSignInWarningDialog
import fr.manyverse.app.presentation.components.onboarding.EmailSignInDialog
import fr.manyverse.app.presentation.components.onboarding.OnboardingAuthButtons
import fr.manyverse.app.presentation.components.onboarding.OtpVerificationDialog
import fr.manyverse.app.presentation.layout.WindowSizeContainer
import fr.manyverse.app.presentation.layout.WindowWidth
import fr.manyverse.app.presentation.layout.platformMaxWidth
import fr.manyverse.app.presentation.theme.Spacing
import fr.manyverse.app.presentation.viewmodel.AuthUiState
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.getString

@Composable
fun OnboardingScreen(
    isDarkTheme: Boolean,
    onToggleTheme: () -> Unit,
    uiState: AuthUiState,
    onEmailChange: (String) -> Unit,
    onSendOtp: () -> Unit,
    onOtpCodeChange: (String) -> Unit,
    onVerifyOtp: () -> Unit,
    onResendOtp: () -> Unit,
    onResetAuthState: () -> Unit,
    onSignInWithGoogle: () -> Unit,
    onSignInWithApple: () -> Unit,
    onSignInAnonymously: () -> Unit,
    onErrorShown: () -> Unit,
    modifier: Modifier = Modifier
) {
    var showEmailDialog by remember { mutableStateOf(false) }
    var showOtpDialog by remember { mutableStateOf(false) }
    var showAnonymousWarningDialog by remember { mutableStateOf(false) }

    // Les dialogues e-mail et OTP affichent eux-mêmes l'erreur. Hors dialogue (Google, Apple,
    // connexion anonyme), elle passe en snackbar puis est effacée : sinon elle réapparaîtrait
    // dans le prochain dialogue ouvert, et une même erreur répétée ne serait plus signalée
    val snackbarHostState = remember { SnackbarHostState() }
    val snackbarScope = rememberCoroutineScope()
    val isDialogShowingError = showEmailDialog || showOtpDialog
    LaunchedEffect(uiState.error, isDialogShowingError) {
        val error = uiState.error ?: return@LaunchedEffect
        if (isDialogShowingError) return@LaunchedEffect
        val message = getString(error)
        onErrorShown()
        snackbarScope.launch { snackbarHostState.showSnackbar(message) }
    }

    // Native sign-in (Credential Manager on Android, AuthenticationServices on iOS).
    // On unsupported platforms (Web/Desktop) or when native fails, fallback to the
    // browser-based OAuth flow handled by AuthViewModel.signInWithOAuth().
    val nativeAuth = rememberNativeAuthLaunchers(
        onGoogleFallback = onSignInWithGoogle,
        onAppleFallback = onSignInWithApple
    )

    LaunchedEffect(uiState.isOtpSent) {
        if (uiState.isOtpSent) {
            showEmailDialog = false
            showOtpDialog = true
        }
    }

    if (showEmailDialog) {
        EmailSignInDialog(
            uiState = uiState,
            onEmailChange = onEmailChange,
            onSendOtp = onSendOtp,
            onDismiss = {
                showEmailDialog = false
                onResetAuthState()
            }
        )
    }

    if (showAnonymousWarningDialog) {
        AnonymousSignInWarningDialog(
            onConfirm = {
                showAnonymousWarningDialog = false
                onSignInAnonymously()
            },
            onDismiss = { showAnonymousWarningDialog = false }
        )
    }

    if (showOtpDialog) {
        OtpVerificationDialog(
            uiState = uiState,
            onOtpCodeChange = onOtpCodeChange,
            onVerifyOtp = onVerifyOtp,
            onResendOtp = onResendOtp,
            onDismiss = {
                showOtpDialog = false
                onResetAuthState()
            }
        )
    }

    Surface(
        modifier = modifier.fillMaxSize(),
        color = MaterialTheme.colorScheme.background
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            ThemeToggleButton(
                isDark = isDarkTheme,
                onClick = onToggleTheme,
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .statusBarsPadding()
                    .padding(Spacing.small)
            )

            Text(
                text = "${AppConfig.APP_VERSION} - ${AppConfig.BUILD_TIME}",
                fontSize = 10.sp,
                color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.4f),
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .navigationBarsPadding()
                    .padding(Spacing.small)
            )

            WindowSizeContainer(
                modifier = Modifier
                    .fillMaxSize()
                    .platformMaxWidth()
                    .align(Alignment.Center)
            ) { windowWidth ->
                if (windowWidth == WindowWidth.COMPACT) {
                    OnboardingPortraitLayout(
                        uiState = uiState,
                        onShowEmailDialog = { showEmailDialog = true },
                        onSignInWithGoogle = nativeAuth.signInWithGoogle,
                        onSignInWithApple = nativeAuth.signInWithApple,
                        onShowAnonymousWarningDialog = { showAnonymousWarningDialog = true }
                    )
                } else {
                    OnboardingLandscapeLayout(
                        uiState = uiState,
                        onShowEmailDialog = { showEmailDialog = true },
                        onSignInWithGoogle = nativeAuth.signInWithGoogle,
                        onSignInWithApple = nativeAuth.signInWithApple,
                        onShowAnonymousWarningDialog = { showAnonymousWarningDialog = true }
                    )
                }
            }

            SnackbarHost(
                hostState = snackbarHostState,
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .navigationBarsPadding()
                    .padding(Spacing.medium)
            )
        }
    }
}

@Composable
private fun OnboardingPortraitLayout(
    uiState: AuthUiState,
    onShowEmailDialog: () -> Unit,
    onSignInWithGoogle: () -> Unit,
    onSignInWithApple: () -> Unit,
    onShowAnonymousWarningDialog: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .safeContentPadding()
            .padding(Spacing.large),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.SpaceBetween
    ) {
        Spacer(modifier = Modifier.height(Spacing.huge))

        OnboardingCarousel(
            modifier = Modifier.weight(1f)
        )

        OnboardingAuthButtons(
            uiState = uiState,
            onShowEmailDialog = onShowEmailDialog,
            onSignInWithGoogle = onSignInWithGoogle,
            onSignInWithApple = onSignInWithApple,
            onShowAnonymousWarningDialog = onShowAnonymousWarningDialog,
            windowWidth = WindowWidth.COMPACT
        )

        Spacer(modifier = Modifier.height(Spacing.large))
    }
}

@Composable
private fun OnboardingLandscapeLayout(
    uiState: AuthUiState,
    onShowEmailDialog: () -> Unit,
    onSignInWithGoogle: () -> Unit,
    onSignInWithApple: () -> Unit,
    onShowAnonymousWarningDialog: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxSize()
            .safeContentPadding()
            .padding(Spacing.large),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Spacing.extraLarge)
    ) {
        OnboardingCarousel(
            modifier = Modifier
                .weight(0.6f)
                .fillMaxHeight()
        )

        Column(
            modifier = Modifier
                .weight(0.4f)
                .fillMaxHeight(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            OnboardingAuthButtons(
                uiState = uiState,
                onShowEmailDialog = onShowEmailDialog,
                onSignInWithGoogle = onSignInWithGoogle,
                onSignInWithApple = onSignInWithApple,
                onShowAnonymousWarningDialog = onShowAnonymousWarningDialog,
                windowWidth = WindowWidth.EXPANDED
            )
        }
    }
}
